import type { ComponentType, ReactNode, SVGProps } from 'react';
import { InboxIcon } from '@heroicons/react/24/outline';
import { cn } from '../cn.js';

export interface EmptyStateProps {
  title: ReactNode;
  description?: ReactNode;
  action?: ReactNode;
  icon?: ComponentType<SVGProps<SVGSVGElement>>;
  className?: string;
}

export function EmptyState({
  title,
  description,
  action,
  icon: Icon = InboxIcon,
  className,
}: EmptyStateProps) {
  return (
    <div
      className={cn(
        'flex flex-col items-center justify-center text-center px-6 py-12 gap-3',
        className,
      )}
    >
      <Icon className="w-8 h-8 text-subtle" aria-hidden="true" />
      <div>
        <p className="text-sm font-medium text-body">{title}</p>
        {description ? <p className="text-sm text-muted mt-1 max-w-sm">{description}</p> : null}
      </div>
      {action}
    </div>
  );
}

export interface ErrorStateProps {
  title?: ReactNode;
  /** Already redacted by the server. Safe to render. */
  message: ReactNode;
  code?: string;
  action?: ReactNode;
  className?: string;
}

export function ErrorState({
  title = 'Something went wrong',
  message,
  code,
  action,
  className,
}: ErrorStateProps) {
  return (
    <div
      role="alert"
      className={cn(
        'flex flex-col items-center justify-center text-center px-6 py-12 gap-3',
        className,
      )}
    >
      <div>
        <p className="text-sm font-semibold text-danger-fg">{title}</p>
        <p className="text-sm text-muted mt-1 max-w-lg">{message}</p>
        {code ? (
          <p className="text-[11px] text-subtle font-mono mt-2" aria-label="Error code">
            {code}
          </p>
        ) : null}
      </div>
      {action}
    </div>
  );
}

/** Skeleton rows for a loading list or table. */
export function SkeletonRows({ rows = 5, className }: { rows?: number; className?: string }) {
  return (
    <div className={cn('space-y-2', className)} aria-hidden="true">
      {Array.from({ length: rows }, (_, i) => (
        <div key={i} className="h-9 rounded-lg bg-raised animate-pulse" />
      ))}
    </div>
  );
}
